/**
 * Application Entry Point
 * 
 * This is the main entry point for the Running Planner React application.
 * It sets up the core providers and routing structure.
 * 
 * Provider Stack:
 * - StrictMode: React development mode for highlighting potential problems
 * - DndProvider: Enables drag-and-drop functionality for reordering workouts
 * - QueryParamProvider: Manages URL query parameters for state persistence
 * - BrowserRouter: Handles client-side routing
 */
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import { DndProvider } from "react-dnd-multi-backend";
import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { QueryParamProvider } from "use-query-params";
import { WindowHistoryAdapter } from "use-query-params/adapters/window";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// Main dashboard + onboarding pages
import Index from "./index";
import RunDashboard from "./dashboard";
import Running from "./running";
import Swimming from "./swimming";
import Cycling from "./cycling";
import Hyrox from "./hyrox";
import Triathlon from "./triathlon";
import Run from "./run"; // Legacy run page for backwards compatibility

// Mount the React application to the DOM
createRoot(document.getElementById("root")!).render(
  <StrictMode>
    {/* DndProvider enables drag-and-drop with support for both mouse and touch */}
    <DndProvider options={HTML5toTouch}>
      {/* QueryParamProvider syncs app state with URL for shareable links */}
      <QueryParamProvider adapter={WindowHistoryAdapter}>
        <div className="app">
          {/* BrowserRouter handles navigation between pages */}
          <BrowserRouter>
            <Routes>
              {/* Index route - Sport Selector */}
              <Route path="/" element={<Index />} />

              {/* Sport onboarding pages */}
              <Route path="/running" element={<Running />} />
              <Route path="/swimming" element={<Swimming />} />
              <Route path="/cycling" element={<Cycling />} />
              <Route path="/hyrox" element={<Hyrox />} />
              <Route path="/triathlon" element={<Triathlon />} />

              {/* Main dashboard */}
              <Route path="/dashboard" element={<RunDashboard />} />

              {/* Legacy route redirects */}
              <Route path="/run" element={<Run />} />
              <Route path="/rungenerate" element={<Navigate to="/running" replace />} />
              <Route path="/onboarding2" element={<Navigate to="/running" replace />} />
              <Route path="/rundashboard" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
        </div>
      </QueryParamProvider>
    </DndProvider>
  </StrictMode>
);
